<template>
    <v-dialog
        v-model="dialogProxy"
        max-width="500px"
        transition="slide-x-transition"
        @click:outside="$emit('close')"
        @keydown.esc="$emit('close')"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="descId">
        <v-card v-focus-trap="dialogProxy">
            <div class="backsheet"></div>

            <!-- Close button -->
            <v-btn class="close-btn" absolute top right icon color="primary" :aria-label="'Close dialog'" type="button" @click="$emit('close')">
                <v-icon aria-hidden="true">mdi-close</v-icon>
            </v-btn>

            <v-card-text class="pt-5 pb-0">
                <div :id="titleId" class="qlbl pb-1 uppercase">Add website URL</div>
                <p :id="descId" class="sr-only">Enter the full URL of the page you want to add. Example: https://www.quaro.io/keyword-recherche</p>

                <!-- Using a form for proper Enter-submit semantics -->
                <form @submit.prevent="saveURL">
                    <v-text-field
                        data-focus-first
                        ref="urlField"
                        class="my-2"
                        rounded
                        filled
                        v-model.trim="localUrl"
                        type="url"
                        inputmode="url"
                        autocomplete="url"
                        :label="'Website URL'"
                        :hint="'e.g. https://www.quaro.io/keyword-recherche'"
                        persistent-hint
                        :aria-describedby="descId"
                        :aria-invalid="Boolean(errorMessage)"
                        :error-messages="errorMessage ? [errorMessage] : []" />
                    <v-card-actions class="pb-2">
                        <v-btn class="mx-auto" color="primary" rounded type="submit" :loading="submitting" :aria-busy="submitting ? 'true' : 'false'"> Add URL </v-btn>
                    </v-card-actions>
                </form>
            </v-card-text>
        </v-card>

        <!-- Live region for feedback -->
        <v-snackbar v-if="notification" top v-model="notification.show" :color="notification.color" role="status" aria-live="polite">
            <v-icon aria-hidden="true">{{ notification.icon }}</v-icon>
            <span class="font-weight-bold pl-2"> {{ notification.message }} </span>
        </v-snackbar>
    </v-dialog>
</template>

<script>
    export default {
        name: "AddUrlDialog",
        props: {
            show: { type: Boolean, default: false },
            id: { type: String, default: "" },
            setMainURL: { type: Boolean, default: false },
            addLighthouseData: { type: Boolean, default: false },
            url: { type: String, default: "" }
        },
        data() {
            return {
                localUrl: "",
                submitting: false,
                notification: null,
                errorMessage: ""
            }
        },
        computed: {
            dialogProxy: {
                get() {
                    return this.show
                },
                set() {
                    // any internal attempt to close should notify parent
                    this.$emit("close")
                }
            },
            titleId() {
                return "add-url-title"
            },
            descId() {
                return "add-url-desc"
            }
        },
        watch: {
            show(val) {
                if (val) {
                    // sync from prop on open
                    this.localUrl = (this.url || "").trim()
                    this.errorMessage = ""
                    this.$nextTick(() => {
                        const el = this.$refs.urlField && this.$refs.urlField.$el
                        if (el) {
                            const input = el.querySelector("input")
                            input && input.focus()
                        }
                    })
                } else {
                    this.submitting = false
                }
            }
        },
        methods: {
            trackEvent(component, action, p1, p2) {
                try {
                    this.$matomo.trackEvent(component, action, p1, p2)
                } catch {
                    /* blocked */
                }
            },
            normalizeUrl(raw) {
                let u = (raw || "").trim()
                if (!u) return ""
                // prepend protocol if missing
                if (!/^https?:\/\//i.test(u)) u = "https://" + u
                return u
            },
            validateUrlOrMessage(u) {
                try {
                    const parsed = new URL(u)
                    if (!/^https?:$/i.test(parsed.protocol)) {
                        return "Please use http or https."
                    }
                    if (!parsed.hostname) {
                        return "Please enter a valid domain."
                    }
                    return "" // valid
                } catch {
                    return "Please enter a complete and accurate website URL."
                }
            },
            async saveURL() {
                this.errorMessage = ""
                const normalized = this.normalizeUrl(this.localUrl)
                const msg = this.validateUrlOrMessage(normalized)
                if (msg) {
                    this.errorMessage = msg
                    this.notification = this.$helpers.createNotification(msg, "mdi-alert", "orange darken-2")
                    return
                }

                try {
                    this.submitting = true
                    const endpoint = process.env.VUE_APP_APIURL + "/cs/add/url"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.id,
                        url: normalized,
                        mainurl: this.setMainURL,
                        addLighthouseData: this.addLighthouseData,
                        user_id: localStorage.id,
                        socket_id: this.$socket.id
                    }
                    this.$emit("started") // parent shows global feedback
                    this.$helpers.axiosPost(endpoint, post_data)
                    this.trackEvent("CS - AddURLDialog", "add", normalized, this.id)
                    // optional local toast
                    this.notification = this.$helpers.createNotification("URL submitted. We are fetching data…", "mdi-check-circle-outline", "primary")
                    // keep dialog open until parent decides; no implicit close for consistency with your flow
                } catch (e) {
                    this.notification = this.$helpers.createNotification("Something went wrong adding your URL! Please try again or contact support.", "mdi-alert", "orange darken-2")
                } finally {
                    this.submitting = false
                }
            }
        }
    }
</script>

<style scoped>
    .close-btn {
        top: 5px;
        right: 5px;
    }

    /* Screenreader-only helper (same util as bei dir) */
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
</style>
